import { create } from 'zustand';
import { devtools } from 'zustand/middleware';

/**
 * Notification Management store — Story 93.1a Task 4.
 *
 * Open/close + active-tab state for the Notification Management side panel
 * (mounted globally by ShellLayout). Mirrors `notificationCenterStore` /
 * `personalSettingsStore` shape — no async logic, no side effects.
 */
export type NotificationManagementTab = 'rules' | 'broadcast' | 'history';

/**
 * Story 93.2a — System broadcast form draft persisted across tab switches.
 * The Tab component is unmounted when the panel switches between Rules /
 * Broadcast / History (see `NotificationManagementPanel.tsx`), so its local
 * state would be lost on every switch. Lifting the draft here lets the form
 * survive `broadcast → rules → broadcast`. Closing the panel resets it.
 */
export interface SystemBroadcastDraft {
  notificationTypeCode: string;
  message: string;
  scheduledAt: string;
}

export const EMPTY_BROADCAST_DRAFT: SystemBroadcastDraft = {
  notificationTypeCode: '',
  message: '',
  scheduledAt: '',
};

interface NotificationManagementStore {
  isOpen: boolean;
  activeTab: NotificationManagementTab;
  /**
   * Live count of rules rendered in the Notification Rules tab. Owned by the
   * tab itself (it calls `setRulesCount` after a successful catalogue load and
   * resets to `null` on load failure / clear). The panel header badge reads
   * from this — keeps the badge truthful when the API errors.
   */
  rulesCount: number | null;
  /**
   * Story 93.2a — live count of currently scheduled broadcasts. Mirrors the
   * `rulesCount` lifecycle: `null` until first successful list load, set on
   * each successful list / create / cancel, reset to `null` on close. The
   * panel header reads from this and hides the badge when `null`.
   */
  broadcastCount: number | null;
  /**
   * Story 93.2a AC #14 — broadcast form draft, persisted across tab switches
   * for the lifetime of the panel. Reset on close.
   */
  broadcastDraft: SystemBroadcastDraft;
  /**
   * Story 93.2a AC #14 — id of the broadcast currently being edited (`null`
   * for create mode). Reset on close.
   */
  broadcastEditingId: string | null;
  /**
   * Bumped every time the form is reset (Clear / successful submit / cancel
   * of edited row). The form's Radix Select uses this as a remount key so the
   * trigger drops its cached label after a reset.
   */
  broadcastFormInstance: number;
  open: () => void;
  close: () => void;
  setTab: (tab: NotificationManagementTab) => void;
  setRulesCount: (count: number | null) => void;
  setBroadcastCount: (count: number | null) => void;
  setBroadcastDraft: (draft: SystemBroadcastDraft) => void;
  setBroadcastEditingId: (id: string | null) => void;
  resetBroadcastForm: () => void;
}

export const useNotificationManagementStore = create<NotificationManagementStore>()(
  devtools(
    (set) => ({
      isOpen: false,
      activeTab: 'rules',
      rulesCount: null,
      broadcastCount: null,
      broadcastDraft: EMPTY_BROADCAST_DRAFT,
      broadcastEditingId: null,
      broadcastFormInstance: 0,
      open: () => set({ isOpen: true, activeTab: 'rules' }, false, 'notificationManagement/open'),
      close: () =>
        set(
          {
            isOpen: false,
            rulesCount: null,
            broadcastCount: null,
            broadcastDraft: EMPTY_BROADCAST_DRAFT,
            broadcastEditingId: null,
          },
          false,
          'notificationManagement/close'
        ),
      setTab: (tab) => set({ activeTab: tab }, false, 'notificationManagement/setTab'),
      setRulesCount: (count) =>
        set({ rulesCount: count }, false, 'notificationManagement/setRulesCount'),
      setBroadcastCount: (count) =>
        set({ broadcastCount: count }, false, 'notificationManagement/setBroadcastCount'),
      setBroadcastDraft: (draft) =>
        set({ broadcastDraft: draft }, false, 'notificationManagement/setBroadcastDraft'),
      setBroadcastEditingId: (id) =>
        set({ broadcastEditingId: id }, false, 'notificationManagement/setBroadcastEditingId'),
      resetBroadcastForm: () =>
        set(
          (s) => ({
            broadcastDraft: EMPTY_BROADCAST_DRAFT,
            broadcastEditingId: null,
            broadcastFormInstance: s.broadcastFormInstance + 1,
          }),
          false,
          'notificationManagement/resetBroadcastForm'
        ),
    }),
    { name: 'notificationManagementStore' }
  )
);
